import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";

export interface ActionSheetOption {
  title: string;
  icon?: string;
  style?: "default" | "destructive" | "cancel";
}

// Display modes for the action sheet
export type ActionSheetDisplayMode = "auto" | "modal" | "prompt" | "desktop";

interface ActionSheetProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  options: ActionSheetOption[];
  onDidDismiss: (result: { index: number }) => void;
}

export function showCustomActionSheet({
  title,
  message,
  options,
  displayMode = "auto"
}: {
  title?: string;
  message?: string;
  options: ActionSheetOption[];
  displayMode?: ActionSheetDisplayMode;
}): Promise<{ index: number }> {
  // Check if we're on a mobile browser or native platform
  const isNativeOrMobile = () => {
    if (typeof window === "undefined") return false;

    // Check for Capacitor (native)
    const isNative = Capacitor.isNativePlatform();

    // Check for mobile browser
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase()
    );

    return isNative || isMobile;
  };

  // Determine which display mode to use based on platform and setting
  const useModalDisplay = () => {
    switch (displayMode) {
      case "auto":
        return isNativeOrMobile();
      case "modal":
        return true;
      case "prompt":
      case "desktop":
        return false;
      default:
        return isNativeOrMobile();
    }
  };

  // Use prompt/simple interface for desktop browsers or when explicitly requested
  if (!useModalDisplay()) {
    return new Promise((resolve) => {
      // Create a simple list dialog for desktop
      let selectedIndex = -1;

      // Create a simple prompt list of options
      const optionList = options.map((opt, idx) => `${idx + 1}. ${opt.title}`).join("\n");
      const promptMessage = `${title || ""}\n\n${message || ""}\n\n${optionList}\n\nEnter option number:`;
      const userResponse = window.prompt(promptMessage, "");

      if (userResponse !== null) {
        const optionNumber = parseInt(userResponse.trim(), 10);
        if (!isNaN(optionNumber) && optionNumber >= 1 && optionNumber <= options.length) {
          selectedIndex = optionNumber - 1;
        }
      }

      // If no valid selection, use cancel option or last option
      if (selectedIndex === -1) {
        const cancelIndex = options.findIndex((option) => option.style === "cancel");
        selectedIndex = cancelIndex !== -1 ? cancelIndex : options.length - 1;
      }

      resolve({ index: selectedIndex });
    });
  }

  // For native platforms and mobile browsers, or when modal is explicitly requested, use our custom UI
  return new Promise((resolve) => {
    // Create a container for our action sheet if it doesn't exist
    let container = document.getElementById("custom-action-sheet-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "custom-action-sheet-container";
      document.body.appendChild(container);
    }

    // Create a state update function
    const handleSelect = ({ index }: { index: number }) => {
      // Clean up
      if (container) {
        const unmountEvent = new CustomEvent("unmountActionSheet");
        document.dispatchEvent(unmountEvent);
      }

      // Resolve the promise with the selected index
      resolve({ index });
    };

    // Render the action sheet
    const actionSheetProps = {
      isOpen: true,
      title,
      message,
      options,
      onDidDismiss: handleSelect
    };

    // Create a temporary component to handle state
    const ActionSheetWrapper = () => {
      const [isVisible, setIsVisible] = useState(true);

      useEffect(() => {
        // Listen for unmount events
        const handleUnmount = () => setIsVisible(false);
        document.addEventListener("unmountActionSheet", handleUnmount);
        return () => {
          document.removeEventListener("unmountActionSheet", handleUnmount);
        };
      }, []);

      if (!isVisible) return null;
      return <CustomActionSheet {...actionSheetProps} />;
    };

    // Render to the container
    const root = document.createElement("div");
    container.appendChild(root);

    // We need to use a dynamic import here to make sure this works with SSR
    import("react-dom/client").then((ReactDOM) => {
      const reactRoot = ReactDOM.createRoot(root);
      reactRoot.render(<ActionSheetWrapper />);
    });
  });
}

// The actual component
export function CustomActionSheet({
  isOpen,
  title,
  message,
  options,
  onDidDismiss
}: ActionSheetProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    }
  }, [isOpen]);

  const handleBackdropClick = () => {
    setIsVisible(false);
    setTimeout(() => {
      // Find the cancel button index, or default to the last option
      const cancelIndex = options.findIndex((option) => option.style === "cancel");
      onDidDismiss({ index: cancelIndex !== -1 ? cancelIndex : options.length - 1 });
    }, 200);
  };

  const handleOptionClick = (index: number) => {
    setIsVisible(false);
    setTimeout(() => {
      onDidDismiss({ index });
    }, 200);
  };

  // Function to render the appropriate icon
  const renderIcon = (icon?: string) => {
    switch (icon) {
      case "camera":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "images":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "folder":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor">
            <path
              fillRule="evenodd"
              d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[120] flex items-end justify-center ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      } transition-opacity duration-300`}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleBackdropClick}
      />

      {/* Action Sheet */}
      <div
        className={`relative w-full max-w-md bg-card rounded-t-xl transform ${
          isVisible ? "translate-y-0" : "translate-y-full"
        } transition-transform duration-300 ease-out`}>
        {/* Handle for dragging - optional */}
        <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 mx-auto mt-2 rounded-full"></div>

        {/* Header */}
        {(title || message) && (
          <div className="px-4 pt-5 pb-2 text-center">
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            )}
            {message && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{message}</p>}
          </div>
        )}

        {/* Options */}
        <div className="px-4 py-3 space-y-2">
          {options.map((option, index) => (
            <button
              key={index}
              className={`w-full flex items-center px-4 py-3 rounded-lg text-left ${
                option.style === "cancel"
                  ? "bg-gray-100 dark:bg-gray-700 font-medium"
                  : option.style === "destructive"
                    ? "text-red-500 dark:text-red-400"
                    : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              } transition-colors`}
              onClick={() => handleOptionClick(index)}>
              {option.icon && (
                <span className="mr-3 text-gray-600 dark:text-gray-300">
                  {renderIcon(option.icon)}
                </span>
              )}
              <span className="text-gray-600 dark:text-gray-300">{option.title}</span>
            </button>
          ))}
        </div>

        {/* Safe area bottom padding for mobile */}
        <div
          className="pb-safe"
          style={{
            height: "var(--safe-area-bottom-fixed, env(safe-area-inset-bottom, 16px))"
          }}></div>
      </div>
    </div>,
    document.body
  );
}
